import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";

import type { Route } from "./+types/root";
import "./app.css";
import { loggerUtils } from "./services/logger";
import ErrorBoundaryWrapper from "./components/ErrorBoundary";

const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];
export default App;
export { links, Layout, ErrorBoundary };

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

import { useEffect, useRef } from "react";
import AppLayout from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import { GlobalSearch } from "./components/GlobalSearch";
import { GlobalUpload } from "./components/GlobalUpload";
import { QueryProvider } from "./components/QueryProvider";
import { AuthProvider, useAuth } from "./components/AuthProvider";

function App() {
  return (
    <QueryProvider>
      <ErrorBoundaryWrapper>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ErrorBoundaryWrapper>
    </QueryProvider>
  );
}

/**
 * AppContent - Renders the appropriate layout based on auth state
 *
 * This is separated from App to allow useAuth() hook usage
 * (hooks must be called inside the provider)
 */
function AppContent() {
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);
  const initialPath = useRef(location.pathname);
  const { isAuthenticated } = useAuth();

  const isLoginPage = location.pathname === '/login';

  // Log app initialization (one time)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      loggerUtils.userAction('app_initialize', {
        url: initialPath.current,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      });
    }
  }, []);

  // Log route changes
  useEffect(() => {
    if (prevLocationRef.current !== location.pathname) {
      const fromPath = prevLocationRef.current;
      const toPath = location.pathname;

      loggerUtils.navigation(fromPath, toPath, {
        search: location.search,
        hash: location.hash,
        state: location.state
      });

      prevLocationRef.current = toPath;
    }
  }, [location.pathname, location.search, location.hash, location.state]);

  return (
    <div className="transition-all duration-300 ease-in-out">
      {isLoginPage ? (
        <Outlet />
      ) : (
        <AppLayout>
          <Outlet />
        </AppLayout>
      )}
      {/* Global components only for authenticated users */}
      {isAuthenticated && !isLoginPage && (
        <>
          <GlobalSearch />
          <GlobalUpload />
        </>
      )}
      <Toaster position="top-center" />
    </div>
  );
}

function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  // Log the error
  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
    
    loggerUtils.error(new Error(`Route error: ${error.status} ${error.statusText}`), 'route_error', {
      status: error.status,
      statusText: error.statusText,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    });
  } else if (error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
    
    loggerUtils.error(error, 'route_boundary', {
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      isDev: import.meta.env.DEV
    });
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
